import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSettingsStore } from '@renderer/stores/settingsStore'
import { getSystemInfo } from '@renderer/utils/systemInfo'
import { useSubscriptionStore } from '@renderer/stores/subscriptionStore'
import { FaExternalLinkAlt, FaPaste } from 'react-icons/fa'

interface UpgradeModalProps {
  onClose: () => void
}

export const UpgradeModal = ({ onClose }: UpgradeModalProps): React.JSX.Element => {
  const { t } = useTranslation()
  const [activationCode, setActivationCode] = useState('')
  const [processing, setProcessing] = useState(false)
  const [isReactivating, setIsReactivating] = useState(false)
  // Use stored deviceId from settingsStore for consistency with validation
  const {
    activationCode: currentActivationCode,
    setActivationCode: storeActivationCode,
    deviceId: storedDeviceId
  } = useSettingsStore()
  const { type: subscriptionType, isValid, computerName, osInfo, endDate } = useSubscriptionStore()

  const handlePaste = async (): Promise<void> => {
    try {
      // Try modern Clipboard API first
      if (navigator.clipboard && navigator.clipboard.readText) {
        const text = await navigator.clipboard.readText()
        setActivationCode(text.trim())
        return
      }

      // Fallback for older browsers or when clipboard API is not available
      // Create a temporary textarea element to access clipboard
      const textArea = document.createElement('textarea')
      textArea.style.position = 'fixed'
      textArea.style.left = '-999999px'
      textArea.style.top = '-999999px'
      document.body.appendChild(textArea)
      textArea.focus()
      textArea.select()

      try {
        const successful = document.execCommand('paste')
        if (successful) {
          setActivationCode(textArea.value.trim())
        } else {
          alert(
            t('projectNav.pasteFailed', 'Failed to paste from clipboard. Please paste manually.')
          )
        }
      } catch {
        alert(
          t(
            'projectNav.pasteNotSupported',
            'Paste functionality not supported in this browser. Please paste manually.'
          )
        )
      } finally {
        document.body.removeChild(textArea)
      }
    } catch (error) {
      console.error('Paste error:', error)
      alert(t('projectNav.pasteError', 'Error accessing clipboard. Please paste manually.'))
    }
  }

  const handleActivate = async (): Promise<void> => {
    try {
      setProcessing(true)

      // Use stored deviceId for consistency with validation in creditsStore
      // This ensures the same deviceId is used for both activation and validation
      const deviceId = storedDeviceId
      const { computerName, osInfo } = getSystemInfo()

      // First try to activate normally
      let response = await fetch(`${process.env.VITE_API_URL}/api/subscription/activate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          token: activationCode,
          deviceId,
          computerName,
          osInfo
        })
      })

      let data = await response.json()

      // If the token is already used, try to change device
      if (!response.ok && data.error === 'Token has already been used') {
        setIsReactivating(true)
        // Try to change device
        response = await fetch(`${process.env.VITE_API_URL}/api/subscription/device`, {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json'
          },
          credentials: 'include',
          body: JSON.stringify({
            token: activationCode,
            deviceId,
            computerName,
            osInfo
          })
        })

        data = await response.json()

        if (!response.ok) {
          throw new Error(
            data.error || t('projectNav.failedReactivate', 'Failed to reactivate subscription')
          )
        }

        alert(t('projectNav.deviceChanged', 'Device changed successfully!'))
      } else if (!response.ok) {
        throw new Error(
          data.error || t('projectNav.failedActivate', 'Failed to activate subscription')
        )
      } else {
        alert(t('projectNav.activatedSuccess', 'Subscription activated successfully!'))
      }

      // Store activation code in settings
      storeActivationCode(activationCode)

      window.location.reload() // Refresh the app to update subscription status
    } catch (error) {
      alert(
        error instanceof Error
          ? error.message
          : t('projectNav.failedActivate', 'Failed to activate subscription')
      )
    } finally {
      setProcessing(false)
      setIsReactivating(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 w-[500px]">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-xl font-bold">
            {t('projectNav.deviceSubscription', 'Device Subscription')}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            title={t('common.close', 'Close')}
            aria-label={t('common.close', 'Close')}
          >
            ×
          </button>
        </div>

        {isValid && subscriptionType && (
          <div className="mb-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
            <h3 className="text-lg font-semibold mb-3 text-blue-600">
              {t('projectNav.activeSubscription', 'Active Subscription')}
            </h3>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span className="text-gray-600">{t('projectNav.planType', 'Plan Type:')}</span>
                <span
                  className={`font-medium bg-clip-text text-transparent ${
                    subscriptionType.toLowerCase() === 'enterprise'
                      ? 'bg-gradient-to-r from-purple-600 to-indigo-600'
                      : subscriptionType.toLowerCase() === 'professional'
                        ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                        : subscriptionType.toLowerCase() === 'starter'
                          ? 'bg-gradient-to-r from-green-500 to-teal-500'
                          : 'bg-gradient-to-r from-gray-500 to-gray-600'
                  }`}
                >
                  {subscriptionType.charAt(0).toUpperCase() + subscriptionType.slice(1)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t('projectNav.computerName', 'Computer Name:')}
                </span>
                <span className="font-medium">{computerName}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">{t('projectNav.osInfo', 'OS Info:')}</span>
                <span className="font-medium">{osInfo}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">
                  {t('projectNav.activationCode', 'Activation Code:')}
                </span>
                <span className="font-medium font-mono">
                  {currentActivationCode.substring(0, 10) + '...'}
                </span>
              </div>
              {endDate && (
                <div className="flex justify-between mt-2 pt-2 border-t border-gray-100">
                  <span className="text-gray-600 text-sm">
                    {t('view.subscriptionValidUntil', { date: endDate })}
                  </span>
                </div>
              )}
            </div>
          </div>
        )}

        <div className="mb-6">
          <h3 className="text-lg font-semibold mb-3">
            {isValid
              ? t('projectNav.changeSubscription', 'Change Subscription')
              : t('projectNav.activateSubscription', 'Activate Subscription')}
          </h3>
          <p className="text-gray-600 mb-4">
            {t(
              'projectNav.enterActivationCode',
              'Enter your activation code to {{action}} this device.',
              {
                action: isReactivating
                  ? t('projectNav.reactivate', 'reactivate')
                  : t('projectNav.activate', 'activate')
              }
            )}
            {isReactivating && (
              <span className="block mt-2 text-sm text-amber-600">
                {t(
                  'projectNav.deactivateWarning',
                  'Note: This will deactivate the subscription on your other device.'
                )}
              </span>
            )}
          </p>
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('projectNav.activationCode', 'Activation Code')}
            </label>
            <div className="relative">
              <input
                type="text"
                value={activationCode}
                onChange={(e) => setActivationCode(e.target.value)}
                className="w-full px-3 py-2 pr-12 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                placeholder={t(
                  'projectNav.enterActivationCodePlaceholder',
                  'Enter your activation code'
                )}
              />
              <button
                type="button"
                onClick={handlePaste}
                className="absolute right-2 top-1/2 transform -translate-y-1/2 p-1 text-gray-500 hover:text-blue-600 transition-colors"
                title={t('projectNav.pasteFromClipboard', 'Paste from clipboard')}
                aria-label={t('projectNav.pasteFromClipboard', 'Paste from clipboard')}
              >
                <FaPaste className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="text-sm text-gray-500">
            <p>{t('projectNav.noCodeQuestion', "Don't have an activation code?")}</p>
            <a
              href={`${process.env.VITE_API_URL}/dashboard/subscription`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-blue-500 hover:text-blue-600 font-medium flex items-center gap-2"
            >
              {t('projectNav.purchaseSubscription', 'Purchase a subscription code')}{' '}
              <FaExternalLinkAlt />
            </a>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-lg bg-gray-200 hover:bg-gray-300"
            disabled={processing}
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            onClick={handleActivate}
            className="px-4 py-2 rounded-lg bg-blue-500 text-white hover:bg-blue-600 disabled:opacity-50"
            disabled={processing || !activationCode.trim()}
          >
            {processing
              ? isReactivating
                ? t('projectNav.reactivating', 'Reactivating...')
                : t('projectNav.activating', 'Activating...')
              : isReactivating
                ? t('projectNav.reactivate', 'Reactivate')
                : t('projectNav.activate', 'Activate')}
          </button>
        </div>
      </div>
    </div>
  )
}
